import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonIcon, IonSkeletonText, LoadingController, ModalController } from '@ionic/angular/standalone';
import { Store } from "@ngrx/store";
import { AccountType, DisplayService, ValidationUtil } from "@qid/core";
import { SexyModule } from '@qid/sexy';
import { AvatarRoundComponent, SuperButtonComponent } from '@qid/super';
import { firstValueFrom } from "rxjs";
import { AccountShare } from '../../../account-share/interface/account-share.interface';
import { AuthSelectors } from '../../../authentication';
import { Organisation } from '../../../organisation';
import { PlanPurchaseService } from '../../../plan-purchase';
import { User } from '../../../user';
import { ShareStatus } from '../../enum/share-status.enum';
import { Share } from '../../model/Share';
import { ViewSharePage } from '../../pages/view-share/view-share.page';
import { ShareService } from '../../services/share.service';


@Component({
  selector: 'share-log-view',
  templateUrl: './share-log-view.component.html',
  styleUrls: ['./share-log-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonIcon,
    IonSkeletonText,
    SexyModule,
    SuperButtonComponent,
    AvatarRoundComponent
  ]
})
export class ShareLogViewComponent implements OnInit {

  @Input() accountShare: AccountShare = null;
  @Input() user: User = null;
  @Input() organisation: Organisation
  @Input() idCard: boolean = false;
  @Input() menuIcon: boolean = true;
  @Input() viewSize: 'small' | 'large' | 'self-collect' = 'large';
  @Input() skeletonOnly: boolean = false;
  @Input() headerOnly: boolean = false;
  @Input() isShowParentAccountName: boolean = false;
  /** When `emit`, row primary action emits `shareActivate` instead of opening `ViewSharePage` (e.g. Chrome extension). */
  @Input() shareRowAction: 'openViewModal' | 'emit' = 'openViewModal';
  @Output() continueClicked = new EventEmitter();
  @Output() shareActivate = new EventEmitter<AccountShare>();

  public sharerCount: number = null;
  public accountToDisplay: User | Organisation | any;
  public accountType: AccountType;
  public activeAccountType$ = this.store.select(AuthSelectors.selectActiveAccountType)

  constructor(
    private shareService: ShareService,
    private modalController: ModalController,
    private displayService: DisplayService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private store: Store,
    private planPurchaseService: PlanPurchaseService
  ) {
  }

  ngOnInit() {
    if (!this.skeletonOnly) {
      this.setRecipient()
    }
  }

  async openViewSharePage() {
    const activeAccountType = await firstValueFrom(this.activeAccountType$);

    if (ValidationUtil.isTrue(this.accountShare?.isQuotaLocked) && activeAccountType == AccountType.organisation) {
      return this.onLockedShareClicked();
    }

    if (this.shareRowAction === 'emit') {
      this.shareActivate.emit(this.accountShare);
      return;
    }

    const modal = await this.modalController.create({
      component: ViewSharePage,
      componentProps: {
        shareId: this.accountShare.shareId,
      },
    });
    await modal.present();
  }


  copyTransactionId(event: Event) {
    event.stopPropagation();
    if (this.accountShare?.isQuotaLocked) {
      this.lockAlert();
      return;
    }
    const el = document.createElement('textarea');
    el.value = this.accountShare.shareId;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    this.displayService.toast({ message: 'C-Form code copied to clipboard!' });
  }

  lockAlert = async () => {
    const alert = await this.alertController.create({
      header: 'Usage Quota Exceeded',
      message: 'This ID exceeds the collection limit.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Upgrade Plan',
          handler: async () => {
            this.planPurchaseService.openPlanUpgrade();
          },
        },
      ],
    });
    await alert.present();
    await alert.onDidDismiss();
  };


  public async onLockedShareClicked() {
    const loader = await this.loadingController.create({
      message: 'Checking Quota to unlock this Collection',
    });
    loader.present();
    const res: Share = await this.shareService.unlock(this.accountShare.shareId);
    loader.dismiss();
    if (res && !ValidationUtil.isTrue(res.isQuotaLocked)) {
      this.accountShare.isQuotaLocked = res.isQuotaLocked;
      this.displayService.toast({
        message: "Unlocked",
        duration: 1000
      });
      return this.openViewSharePage()
    }
    const alert = await this.alertController.create({
      header: 'Usage Quota Exceeded',
      subHeader: 'This ID exceeds the collection limit.',
      buttons: [
        {
          text: 'Upgrade Plan',
          handler: () => {
            this.planPurchaseService.openPlanUpgrade();
          },
        },
        {
          text: 'Cancel',
          handler: () => {
          },
        },
      ],
    });
    return alert.present();
  }

  async setRecipient() {


    if (!this.accountShare?.summary) {
      return
    }

    this.accountType = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountType))


    const participant = this.accountType == AccountType.organisation ?
      this.accountShare?.summary?.mainParticipant :
      this.accountShare?.summary?.organisingParticipant
    this.accountToDisplay = participant

  }


  getParticipantsCount() {
    if (!this.accountShare || !this.accountShare.summary?.totalParticipants) {
      return null;
    }
    this.sharerCount = this.accountShare.summary.totalParticipants - 1;
    return this.sharerCount;
  }


  getShareSummary() {
    return this.accountShare?.formName;
  }

  getShareStatus(accountShare: AccountShare) {
    switch (accountShare.status) {
      case ShareStatus.completed:
        return "Completed";
      case ShareStatus.open:
        return "Pending";
      case ShareStatus.cancelled:
        return "Cancelled";
    }
  }

  getShareStatusIcon(accountShare: AccountShare) {
    switch (accountShare.status) {
      case ShareStatus.completed:
        return "checkmark-circle";
      case ShareStatus.open:
        return "alert-circle";
      case ShareStatus.cancelled:
        return "close-circle";
    }
  }

  getShareStatusColor(accountShare: AccountShare) {
    switch (accountShare.status) {
      case ShareStatus.completed:
        return "success";
      case ShareStatus.open:
        return "warning";
      case ShareStatus.cancelled:
        return "two";
    }
  }

  protected readonly ShareStatus = ShareStatus;

  isShowCFormButton(purpose: string | undefined) {
    return purpose?.toLowerCase()?.indexOf('foreign') > -1
  }
}
